import { useMutation, useQuery } from '@tanstack/react-query'
import type {
	CalibrationOrigin,
	InteracaoPersonRole,
	KpiFreeTextTableParams,
	KpiMetricsParams,
} from '@/types'
import {
	exportDdsAttendance,
	exportDirecionadaAnswers,
	getCalibrationDrilldown,
	getDdsAttendanceDrilldown,
	getDdsMetrics,
	getDirecionadaMetrics,
	getFreeTextMetrics,
	getInteracaoMetrics,
	getInteracaoPersonDrilldown,
	getKpiMetrics,
	getQuizQuestionsDrilldown,
	getVereAgirMetrics,
} from '../services/kpi-metrics'

export const kpiMetricsKeys = {
	all: ['kpi-metrics'] as const,
	detail: (params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, params] as const,
	vereAgir: (params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'vereagir', params] as const,
	direcionada: (params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'direcionada', params] as const,
	interacao: (params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'interacao', params] as const,
	freeText: (params?: KpiMetricsParams & KpiFreeTextTableParams) =>
		[...kpiMetricsKeys.all, 'free-text', params] as const,
	dds: (params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'dds', params] as const,
	calibration: (
		personId?: string,
		origin?: CalibrationOrigin,
		params?: KpiMetricsParams,
	) =>
		[...kpiMetricsKeys.all, 'calibration', personId, origin, params] as const,
	quizQuestions: (quizId?: string, params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'quiz-questions', quizId, params] as const,
	ddsAttendance: (crId?: string, params?: KpiMetricsParams) =>
		[...kpiMetricsKeys.all, 'dds-attendance', crId, params] as const,
	interacaoPersonDrill: (
		personId?: string,
		role?: InteracaoPersonRole,
		params?: KpiMetricsParams,
	) =>
		[
			...kpiMetricsKeys.all,
			'interacao-person-drill',
			personId,
			role,
			params,
		] as const,
}

export function useKpiMetrics(params?: KpiMetricsParams, enabled = true) {
	return useQuery({
		queryKey: kpiMetricsKeys.detail(params),
		queryFn: () => getKpiMetrics(params),
		staleTime: 60 * 1000,
		enabled,
	})
}

export function useVereAgirMetrics(params?: KpiMetricsParams) {
	return useQuery({
		queryKey: kpiMetricsKeys.vereAgir(params),
		queryFn: () => getVereAgirMetrics(params),
		staleTime: 60 * 1000,
	})
}

export function useDirecionadaMetrics(params?: KpiMetricsParams) {
	return useQuery({
		queryKey: kpiMetricsKeys.direcionada(params),
		queryFn: () => getDirecionadaMetrics(params),
		staleTime: 60 * 1000,
	})
}

export function useInteracaoMetrics(params?: KpiMetricsParams) {
	return useQuery({
		queryKey: kpiMetricsKeys.interacao(params),
		queryFn: () => getInteracaoMetrics(params),
		staleTime: 60 * 1000,
	})
}

export function useFreeTextMetrics(
	params?: KpiMetricsParams & KpiFreeTextTableParams,
) {
	return useQuery({
		queryKey: kpiMetricsKeys.freeText(params),
		queryFn: () => getFreeTextMetrics(params),
		staleTime: 60 * 1000,
	})
}

export function useExportDirecionadaAnswers() {
	return useMutation({ mutationFn: exportDirecionadaAnswers })
}

export function useDdsMetrics(params?: KpiMetricsParams) {
	return useQuery({
		queryKey: kpiMetricsKeys.dds(params),
		queryFn: () => getDdsMetrics(params),
		staleTime: 60 * 1000,
	})
}

export function useCalibrationDrilldown(
	personId: string | undefined,
	origin: CalibrationOrigin | undefined,
	params?: KpiMetricsParams,
) {
	return useQuery({
		queryKey: kpiMetricsKeys.calibration(personId, origin, params),
		queryFn: () =>
			getCalibrationDrilldown(
				personId as string,
				origin as CalibrationOrigin,
				params,
			),
		enabled: !!personId && !!origin,
		staleTime: 60 * 1000,
	})
}

export function useQuizQuestionsDrilldown(
	quizId: string | undefined,
	params?: KpiMetricsParams,
) {
	return useQuery({
		queryKey: kpiMetricsKeys.quizQuestions(quizId, params),
		queryFn: () => getQuizQuestionsDrilldown(quizId as string, params),
		enabled: !!quizId,
		staleTime: 60 * 1000,
	})
}

export function useDdsAttendanceDrilldown(
	crId: string | undefined,
	params?: KpiMetricsParams,
) {
	return useQuery({
		queryKey: kpiMetricsKeys.ddsAttendance(crId, params),
		queryFn: () => getDdsAttendanceDrilldown(crId as string, params),
		enabled: !!crId,
		staleTime: 60 * 1000,
	})
}

export function useInteracaoPersonDrilldown(
	personId: string | undefined,
	role: InteracaoPersonRole | undefined,
	params?: KpiMetricsParams,
) {
	return useQuery({
		queryKey: kpiMetricsKeys.interacaoPersonDrill(personId, role, params),
		queryFn: () =>
			getInteracaoPersonDrilldown(
				personId as string,
				role as InteracaoPersonRole,
				params,
			),
		enabled: !!personId && !!role,
		staleTime: 60 * 1000,
	})
}

export function useExportDdsAttendance() {
	return useMutation({
		mutationFn: ({
			crId,
			params,
		}: {
			crId: string
			params?: KpiMetricsParams
		}) => exportDdsAttendance(crId, params),
	})
}
